import { create } from "zustand";

export const DEFAULT_TAB_CONFIG = {
  enrichment: "dynamic",
  limit: 6,
  manuallyPicked: [],
  sorting: "created",
  contributors: [],
  labels: [],
  spaces: [],
  restrictedToCollections: [],
  useCollections: false,
  selectedCollections: [],
};

export const useNewsStore = create((set, get) => ({
  data: {},
  setData: (value, isUndo = false, isRedo = false) => {
    const currentData = get().data;
    const newData = { ...currentData, ...value };

    if (!isUndo && !isRedo) {
      get().pushToHistory(newData);
    }

    set(() => ({
      data: newData,
    }));
  },

  getData: () => get().data,

  selectedTab: undefined,
  setSelectedTab: (id) => set({ selectedTab: id }),

  getSelectedTab: (returnWholeTab = false) => {
    const { selectedTab, data } = get();
    if (!data?.tabs?.length) return;

    if (!selectedTab) {
      set({ selectedTab: data.tabs[0].id });
      return returnWholeTab ? data.tabs[0] : data.tabs[0].id;
    }
    return returnWholeTab
      ? data.tabs.find((tab) => tab.id === selectedTab)
      : selectedTab;
  },

  updateConfig: ({ value, key }) => {
    const currentData = { ...get().data };

    let newData = {};
    if (key) {
      newData = {
        ...currentData,
        config: {
          ...currentData.config,
          [key]: value,
        },
      };
    } else {
      newData = {
        ...currentData,
        config: {
          ...currentData.config,
          ...value,
        },
      };
    }

    get().pushToHistory(newData);

    set(() => ({
      data: newData,
    }));
  },

  updateConfigTab: ({ value }) => {
    const currentData = { ...get().data };
    const selectedTab = get().getSelectedTab();

    const newData = {
      ...currentData,
      config: {
        ...currentData.config,
        tabs: {
          ...currentData.config.tabs,
          [selectedTab]: {
            ...currentData.config.tabs[selectedTab],
            ...value,
          },
        },
      },
    };

    get().pushToHistory({ ...newData });
    set(() => ({
      data: newData,
    }));
  },

  updateTabs: (value) => {
    const newData = { ...get().data, tabs: [...value] };
    get().pushToHistory(newData);
    set(() => ({ data: newData }));
  },

  deleteTab: (tabId) =>
    set(() => {
      const data = { ...get().data };
      const selectedTab = get().getSelectedTab();
      const { tabs, config } = data;

      const updatedTabs = tabs.filter((t) => t.id.toString() !== tabId);
      const updatedConfigTabs = Object(config.tabs);
      delete updatedConfigTabs[tabId];

      const newData = {
        ...data,
        config: {
          ...data.config,
          tabs: updatedConfigTabs,
        },
        tabs: [...updatedTabs],
      };

      get().pushToHistory(newData);
      return {
        data: newData,
        selectedTab: selectedTab === tabId ? updatedTabs[0]?.id : selectedTab,
      };
    }),

  createNewTab: () => {
    const data = { ...get().data };
    const { tabs, config } = data;

    const lastTabPosition = tabs.sort((tabA, tabB) =>
      tabA.position > tabB.position ? 1 : -1
    )[tabs.length - 1].position;

    const tabId = crypto.randomUUID();
    const newTab = {
      id: tabId,
      name: "",
      position: lastTabPosition + 1,
    };

    const newData = {
      ...data,
      config: {
        ...config,
        tabs: {
          ...config.tabs,
          [tabId]: DEFAULT_TAB_CONFIG,
        },
      },
      tabs: [...tabs, newTab],
    };

    get().pushToHistory(newData);
    set(() => ({
      data: newData,
      selectedTab: tabId,
    }));
  },

  position: 0,
  updatePosition: (value) => {
    set(() => ({
      position: value,
    }));
  },

  history: [],
  pushToHistory: (value) => {
    const newHistory = [...get().history, value];

    set(() => ({
      history: newHistory,
      position: newHistory.length - 1,
    }));
  },

  undo: () => {
    const { history, position, setData } = get();
    setData(
      {
        ...history[position - 1],
      },
      true,
      false
    );
    set({ position: position - 1 });
  },
  redo: () => {
    const { history, position, setData } = get();
    setData(
      {
        ...history[position + 1],
      },
      false,
      true
    );
    set({ position: position + 1 });
  },

  canUndo: () => {
    const { position } = get();
    return position > 0;
  },
  canRedo: () => {
    const { history, position } = get();
    return position < history.length - 1;
  },

  resetStore: () => {
    set({
      data: {},
      position: 0,
      history: [],
    });
  },
}));
